import { z } from 'zod';

/** US-101 AC-6: wszystkie czasy edycji w tej strefie. */
export const EVENT_TIME_ZONE = 'Europe/Warsaw';

/** F-105, dziennik pkt 16: szkic → opublikowana → zakończona. */
export const editionStatuses = ['draft', 'published', 'finished'] as const;
export const editionStatusSchema = z.enum(editionStatuses);
export type EditionStatus = z.infer<typeof editionStatusSchema>;

/** US-101 AC-1: krótki identyfikator do adresów URL, np. `2027-krakow`. */
export const editionSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, { message: 'Tylko małe litery bez polskich znaków, cyfry i myślniki, np. 2027-krakow.' })
  .min(3, { message: 'Co najmniej 3 znaki.' })
  .max(60, { message: 'Maksymalnie 60 znaków.' });

export const localDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Podaj datę.' });
export const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'Podaj godzinę (GG:MM).' });

const requiredText = (max: number) =>
  z.string().trim().min(1, { message: 'To pole jest wymagane.' }).max(max, { message: `Maksymalnie ${max} znaków.` });

/** Kolor w zapisie `#rrggbb` (trafia do stylu, więc tylko ten format); pusty – brak koloru. */
const editionAccentColorSchema = z
  .string()
  .trim()
  .toLowerCase()
  .optional()
  .transform((value) => (value ? value : undefined))
  .pipe(z.string().regex(/^#[0-9a-f]{6}$/, { message: 'Podaj kolor w zapisie #rrggbb.' }).optional());

/** Opcjonalny link do strony zewnętrznej – trafia do `href`, więc tylko http(s), bez `javascript:` i podobnych. */
const optionalLinkSchema = (example: string) =>
  z
    .string()
    .trim()
    .max(500)
    .optional()
    .transform((value) => (value ? value : undefined))
    .pipe(z.string().url({ protocol: /^https?$/, message: `Podaj pełny adres, np. ${example}` }).optional());

/**
 * US-101 AC-1 i US-102: dane edycji z formularza admina. Daty i godziny są lokalne (Europe/Warsaw) – serwer zamienia je
 * na chwile w czasie w jednym miejscu (ADR 02, T8).
 */
export const editionInputSchema = z
  .object({
    slug: editionSlugSchema,
    name: requiredText(120),
    city: requiredText(80),
    address: requiredText(200),
    /** Krótka nazwa miejsca, np. „SP Kopernika” – odróżnia edycje w tym samym mieście tego samego dnia (PRD §10 pkt 49). */
    placeName: z.string().trim().max(40, { message: 'Maksymalnie 40 znaków.' }).optional().transform((value) => value || undefined),
    mapUrl: optionalLinkSchema('https://maps.app.goo.gl/…'),
    /** Plan budynku (PDF) – link do pliku, każda edycja ma swój (PRD §10 pkt 39). */
    floorPlanUrl: optionalLinkSchema('https://…/plan-budynku.pdf'),
    date: localDateSchema,
    startTime: localTimeSchema,
    endTime: localTimeSchema,
    registrationOpensDate: localDateSchema,
    registrationOpensTime: localTimeSchema,
    defaultReserve: z.coerce.number().int().min(0, { message: 'Nie mniej niż 0.' }).max(50, { message: 'Nie więcej niż 50.' }),
    /** Kolejność na liście edycji w obrębie dnia – mniejsza wyżej (PRD §10 pkt 43). */
    displayOrder: z.coerce
      .number()
      .int({ message: 'Podaj liczbę całkowitą.' })
      .min(0, { message: 'Nie mniej niż 0.' })
      .max(999, { message: 'Nie więcej niż 999.' })
      .default(0),
    /** Kolor paska u góry karty edycji na liście (`#rrggbb`); pusty – kolor zależny od stanu zapisów (PRD §10 pkt 44). */
    accentColor: editionAccentColorSchema,
  })
  .refine((value) => value.endTime > value.startTime, {
    path: ['endTime'],
    message: 'Zakończenie musi być po rozpoczęciu.',
  });
export type EditionInput = z.infer<typeof editionInputSchema>;

// --- Materiały edycji (PRD §10 pkt 40) ---

/**
 * Pliki i strony dla uczestników edycji (np. mapa i instrukcja labiryntu zagadek) – kafelki w karcie wydarzenia i linki
 * w przypomnieniu. Sam plik leży poza aplikacją, w bazie tylko adres.
 */
export const editionMaterialInputSchema = z.object({
  title: requiredText(80),
  hint: z.string().trim().max(80, { message: 'Maksymalnie 80 znaków.' }).optional().transform((value) => value || undefined),
  url: z
    .string()
    .trim()
    .max(500)
    .pipe(z.string().url({ protocol: /^https?$/, message: 'Podaj pełny adres, np. https://…/labirynt.pdf' })),
});
export type EditionMaterialInput = z.infer<typeof editionMaterialInputSchema>;

export const editionMaterialSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  hint: z.string().nullable(),
  url: z.string(),
});
export type EditionMaterial = z.infer<typeof editionMaterialSchema>;
export const editionMaterialListSchema = z.object({ materials: z.array(editionMaterialSchema) });
export type EditionMaterialList = z.infer<typeof editionMaterialListSchema>;
export const editionMaterialMoveSchema = z.object({ direction: z.enum(['up', 'down']) });
export type EditionMaterialMove = z.infer<typeof editionMaterialMoveSchema>;

/** Edycja widoczna publicznie (US-104). Czasy jako ISO 8601 – interfejs pokazuje je w strefie Europe/Warsaw. */
export const editionSummarySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  city: z.string(),
  address: z.string(),
  placeName: z.string().nullable(),
  mapUrl: z.string().nullable(),
  floorPlanUrl: z.string().nullable(),
  /** Kolor paska na karcie edycji (`#rrggbb`) albo `null` – kolor zależny od stanu zapisów. */
  accentColor: z.string().nullable(),
  date: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  registrationOpensAt: z.string(),
  status: editionStatusSchema,
  /** Zapisy otwarte dla wszystkich (data otwarcia minęła, edycja opublikowana). */
  registrationOpen: z.boolean(),
  /** Tylko dla zalogowanego: konto ma wczesny dostęp do tej edycji (BR-39). */
  earlyAccess: z.boolean(),
  /** Tylko dla zalogowanego: konto jest zgłoszone do tej edycji (US-104 AC-3). */
  registered: z.boolean(),
  /** US-106 AC-2: informacje organizacyjne i zastrzeżenia (oczyszczony HTML) – tylko w szczegółach edycji. */
  infoHtml: z.string().nullable().optional(),
  noticeHtml: z.string().nullable().optional(),
  /** PRD §10 pkt 40: materiały edycji w kolejności – tylko w szczegółach edycji. */
  materials: z.array(editionMaterialSchema).optional(),
});
export type EditionSummary = z.infer<typeof editionSummarySchema>;
export const editionListSchema = z.object({ editions: z.array(editionSummarySchema) });

/** Edycja w panelu admina: pola formularza (lokalne) + stan. */
export const adminEditionSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  city: z.string(),
  address: z.string(),
  placeName: z.string().nullable(),
  mapUrl: z.string().nullable(),
  floorPlanUrl: z.string().nullable(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  registrationOpensDate: z.string(),
  registrationOpensTime: z.string(),
  defaultReserve: z.number().int(),
  displayOrder: z.number().int(),
  accentColor: z.string().nullable(),
  status: editionStatusSchema,
  /** US-101 AC-1: identyfikatora nie można zmienić po pierwszej publikacji. */
  slugLocked: z.boolean(),
  registeredAccounts: z.number().int(),
  registeredPeople: z.number().int(),
  statusHistory: z.array(
    z.object({
      from: editionStatusSchema.nullable(),
      to: editionStatusSchema,
      at: z.string(),
      actor: z.string().nullable(),
    }),
  ),
});
export type AdminEdition = z.infer<typeof adminEditionSchema>;
export const adminEditionListSchema = z.object({ editions: z.array(adminEditionSchema) });

export const changeEditionStatusSchema = z.object({ status: editionStatusSchema });
export type ChangeEditionStatus = z.infer<typeof changeEditionStatusSchema>;

/**
 * Powód odrzucenia zmiany daty albo miasta (US-101 AC-4). `same_day` – osoba ma zapisy tego dnia w edycji w innym mieście;
 * `overlap` – zajęcia osoby nachodzą na jej zajęcia w innej edycji tego dnia albo są za blisko (BR-17, bufor przejścia).
 */
export const editionDateConflictSchema = z.object({
  participant: z.string(),
  reason: z.enum(['age', 'same_day', 'overlap']),
  otherEdition: z.string().optional(),
});
export type EditionDateConflict = z.infer<typeof editionDateConflictSchema>;

// --- Wczesny dostęp (US-105, US-409) ---

export const earlyAccessPanelSchema = z.object({
  link: z.object({ url: z.string(), createdAt: z.string() }).nullable(),
  accounts: z.array(
    z.object({
      accountId: z.string().uuid(),
      email: z.string(),
      name: z.string(),
      grantedAt: z.string(),
      registeredPeople: z.number().int(),
    }),
  ),
});
export type EarlyAccessPanel = z.infer<typeof earlyAccessPanelSchema>;

export const earlyAccessRedeemResponseSchema = z.object({
  slug: z.string(),
  name: z.string(),
  /** `granted` – konto dostało dostęp; `already_open` – zapisy są już otwarte dla wszystkich (US-409 AC-5). */
  result: z.enum(['granted', 'already_open']),
});
export type EarlyAccessRedeemResponse = z.infer<typeof earlyAccessRedeemResponseSchema>;
